import { PremiumFeature } from "@/components-legacy/premium-feature";
import Header from "@/components/layouts/header";
import { useShopFlag } from "@/hooks/use-flag";
import useAddWorkflow from "@babackapp/private-api-client/client/use-add-workflow";
import useDeleteWorkflow from "@babackapp/private-api-client/client/use-delete-workflow";
import useShop from "@babackapp/private-api-client/client/use-shop";
import useEditWorkflow from "@babackapp/private-api-client/client/use-update-workflow";
import { useWorkflowsListQuery } from "@babackapp/private-api-client/client/use-workflows-query";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { TriangleAlert } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { AddWorkFlowDialog } from "./forms/add-workflow-dialog";
import { WorkflowsPageProvider } from "./use-workflows-context";
import { WorkflowDataTable } from "./workflow-data-table";

export default function WorkflowsPage() {
  const t = useTranslation();
  const params = useParams<{ shopId: string }>();
  const shopId = params.shopId || "";
  const { data: shop } = useShop(shopId);
  const { data: workflowList } = useWorkflowsListQuery(shopId);
  const { mutateAsync: addWorkflow } = useAddWorkflow(shopId);
  const { mutateAsync: updateWorkflow } = useEditWorkflow(shopId);
  const { mutate: deleteWorkflowMutation } = useDeleteWorkflow(shopId);
  // V1 creation freeze (BAB-2878), tied to the return-workflows rollout:
  // once the shop has the new editor, no new legacy workflow — existing
  // ones stay editable until the migration.
  const isCreationFrozen = useShopFlag("return-workflows");
  const newEditorPath = `/shops/${shopId}/settings/notifications/workflows`;

  if (!shopId || !shop || !workflowList) {
    return null;
  }

  return (
    <WorkflowsPageProvider
      shopId={shopId}
      workflowList={workflowList}
      onAddWorkflow={addWorkflow}
      onRemoveWorkflow={deleteWorkflowMutation}
      onUpdateWorkflow={updateWorkflow}
    >
      <div className="w-full">
        {shop.plan.workflows && workflowList.length > 0 ? (
          <>
            <Header
              title={t("workflow.title")}
              rightContent={
                isCreationFrozen ? (
                  <Button asChild>
                    <Link to={newEditorPath}>
                      {t("workflow.deprecation.cta")}
                    </Link>
                  </Button>
                ) : (
                  <AddWorkFlowDialog>
                    <Button>{t("workflow.create.create")}</Button>
                  </AddWorkFlowDialog>
                )
              }
            />
            {isCreationFrozen && (
              <div className="mx-4 mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700 sm:mx-6 md:mx-8">
                <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                <span>
                  {t("workflow.deprecation.banner")}{" "}
                  <Link to={newEditorPath} className="font-medium underline">
                    {t("workflow.deprecation.bannerLink")}
                  </Link>
                </span>
              </div>
            )}
            <WorkflowDataTable />
          </>
        ) : (
          <div className="h-screen py-6">
            <div className="h-full px-4 mx-auto max-w-7xl sm:px-6 md:px-8">
              <EmptyWorkflow
                locked={!shop.plan.workflows}
                frozen={isCreationFrozen}
                newEditorPath={newEditorPath}
              />
            </div>
          </div>
        )}
      </div>
    </WorkflowsPageProvider>
  );
}

function EmptyWorkflow({
  locked,
  frozen,
  newEditorPath,
}: {
  locked: boolean;
  frozen: boolean;
  newEditorPath: string;
}) {
  const t = useTranslation();

  return (
    <div className="flex justify-center h-full">
      <div className="flex flex-col items-center justify-center space-y-8">
        <div className="space-y-4">
          {[...Array(3)].map((_, index) => (
            <div
              key={index}
              className="flex w-56 p-3 bg-white rounded shadow-2xl shadow-gray-200"
            >
              <div className="w-7 h-7 bg-gray-50 rounded-md"></div>
              <div className="flex-1 ml-3 space-y-2">
                <div className="h-2.5 w-24 bg-gray-100 rounded-md"></div>
                <div className="h-2.5 w-18 bg-gray-50 rounded-md"></div>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center space-y-2">
          <div className="text-sm font-medium text-gray-700">
            {t("workflow.empty.title")}
          </div>
          <div className="text-sm text-gray-400">
            {t("workflow.empty.subtitle")}
          </div>
        </div>
        {locked ? (
          <PremiumFeature />
        ) : frozen ? (
          <Button asChild>
            <Link to={newEditorPath}>{t("workflow.deprecation.cta")}</Link>
          </Button>
        ) : (
          <AddWorkFlowDialog>
            <Button>{t("workflow.empty.create")}</Button>
          </AddWorkFlowDialog>
        )}
      </div>
    </div>
  );
}
